import React, { useState, useEffect } from 'react';
import { Form, Button } from 'react-bootstrap';
import { Save, Key, Eye, EyeOff, Monitor, CheckCircle, AlertTriangle, Clock, Timer, LayoutList } from 'lucide-react';
import MainLayout from '../components/MainLayout';
import ThemeSelector from '../components/ThemeSelector';
import api from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { menuItems, ALWAYS_VISIBLE_PATHS } from '../config/menuItems';
import { pageVisibilityAPI } from '../services/pageVisibilityApi';

const ToggleSwitch = ({ checked, onChange, disabled }: { checked: boolean; onChange: () => void; disabled?: boolean }) => (
  <button
    onClick={onChange}
    disabled={disabled}
    style={{
      width: '38px', height: '21px', borderRadius: '999px', border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
      background: checked ? '#10b981' : '#d1d5db', position: 'relative', transition: 'background 0.15s', opacity: disabled ? 0.5 : 1,
    }}
  >
    <div style={{
      width: '17px', height: '17px', borderRadius: '50%', background: '#fff', position: 'absolute', top: '2px',
      left: checked ? '19px' : '2px', transition: 'left 0.15s', boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
    }} />
  </button>
);

const SettingsPage: React.FC = () => {
  const { user } = useAuth();

  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [showPw, setShowPw] = useState({ current: false, new: false, confirm: false });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwMsg, setPwMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Mandatory end-of-day exit cutoffs — client's explicit ask: past this time, open positions
  // MUST be force-closed rather than just left unmanaged once trading hours end. Two separate
  // times since Cash Market (real NSE_EQ intraday stock positions) carries the broker's own
  // stricter auto-square-off penalty window and needs to close earlier than F&O.
  const [exitTimes, setExitTimes] = useState({ spread_others_exit_time: '15:25', cash_market_exit_time: '15:02' });
  const [exitTimesSaving, setExitTimesSaving] = useState(false);
  const [exitTimesMsg, setExitTimesMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Single trading-entry window shared by every feature — NSE, BSE, NY Spread, SX Spread,
  // Manual Spread, Option Selling, Cash Market. Moved here from the per-exchange start/end
  // time pickers that used to live in the top app bar (those let NSE and BSE drift apart from
  // each other and never covered Option Selling/Cash Market at all).
  const [tradingHours, setTradingHours] = useState({ startTime: '09:15', endTime: '15:30' });
  const [tradingHoursSaving, setTradingHoursSaving] = useState(false);
  const [tradingHoursMsg, setTradingHoursMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [activeSection, setActiveSection] = useState('tradingHours');

  // Sidebar menu customization — client's explicit ask: pick which pages show in the left
  // sidebar, hide the rest. Keyed by the sidebar item's own route path; no entry = visible.
  const [hiddenPaths, setHiddenPaths] = useState<Set<string>>(new Set());
  const [pageVisSavingPath, setPageVisSavingPath] = useState<string | null>(null);
  const [pageVisMsg, setPageVisMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    api.get('/mandatory-exit-settings').then(res => {
      setExitTimes({
        spread_others_exit_time: (res.data.spread_others_exit_time || '15:25:00').substring(0, 5),
        cash_market_exit_time: (res.data.cash_market_exit_time || '15:02:00').substring(0, 5),
      });
    }).catch(() => {});
    api.get('/trading-hours').then(res => {
      setTradingHours({
        startTime: (res.data.startTime || '09:15:00').substring(0, 5),
        endTime: (res.data.endTime || '15:30:00').substring(0, 5),
      });
    }).catch(() => {});
    pageVisibilityAPI.list().then(res => {
      setHiddenPaths(new Set(res.data.filter(p => !p.is_visible).map(p => p.page_key)));
    }).catch(() => {});
  }, []);

  const handlePageVisibilityToggle = async (path: string, currentlyVisible: boolean) => {
    const nextVisible = !currentlyVisible;
    setPageVisSavingPath(path);
    try {
      await pageVisibilityAPI.set(path, nextVisible);
      setHiddenPaths(prev => {
        const next = new Set(prev);
        if (nextVisible) next.delete(path); else next.add(path);
        return next;
      });
      // MainLayout (which wraps this very page) owns its own separate copy of this list for the
      // sidebar — without this, the toggle here wouldn't be reflected there until a full refresh.
      window.dispatchEvent(new Event('page-visibility-changed'));
    } catch (err: any) {
      showMsg(setPageVisMsg, 'error', err.response?.data?.error || 'Failed to update page visibility');
    } finally {
      setPageVisSavingPath(null);
    }
  };

  const handleTradingHoursSave = async () => {
    try {
      setTradingHoursSaving(true);
      await api.put('/trading-hours', tradingHours);
      showMsg(setTradingHoursMsg, 'success', 'Trading hours saved successfully');
    } catch (err: any) {
      showMsg(setTradingHoursMsg, 'error', err.response?.data?.error || 'Failed to save trading hours');
    } finally { setTradingHoursSaving(false); }
  };

  const handleExitTimesSave = async () => {
    try {
      setExitTimesSaving(true);
      await api.put('/mandatory-exit-settings', exitTimes);
      showMsg(setExitTimesMsg, 'success', 'Exit times saved successfully');
    } catch (err: any) {
      showMsg(setExitTimesMsg, 'error', err.response?.data?.error || 'Failed to save exit times');
    } finally { setExitTimesSaving(false); }
  };

  const showMsg = (setter: any, type: 'success' | 'error', text: string) => {
    setter({ type, text });
    setTimeout(() => setter(null), 3000);
  };

  const handleChangePassword = async () => {
    if (!pwForm.current_password || !pwForm.new_password || !pwForm.confirm_password) { showMsg(setPwMsg, 'error', 'All fields are required'); return; }
    if (pwForm.new_password !== pwForm.confirm_password) { showMsg(setPwMsg, 'error', 'Passwords do not match'); return; }
    if (pwForm.new_password.length < 6) { showMsg(setPwMsg, 'error', 'Min 6 characters required'); return; }
    try {
      setPwLoading(true);
      await api.post('/auth/change-password', pwForm);
      showMsg(setPwMsg, 'success', 'Password changed successfully');
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err: any) {
      showMsg(setPwMsg, 'error', err.response?.data?.error || 'Failed to change password');
    } finally { setPwLoading(false); }
  };

  const navItems = [
    { id: 'tradingHours', label: 'Trading Hours', icon: Timer },
    { id: 'exitTimes', label: 'Exit Times', icon: Clock },
    { id: 'pageVisibility', label: 'Page Visibility', icon: LayoutList },
    { id: 'password', label: 'Change Password', icon: Key },
    { id: 'appearance', label: 'Appearance', icon: Monitor },
  ];

  const MsgBox = ({ msg }: { msg: { type: string; text: string } | null }) => !msg ? null : (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '8px',
      padding: '8px 12px', borderRadius: '8px', marginBottom: '14px', fontSize: '12px',
      background: msg.type === 'success' ? '#f0fdf4' : '#fef2f2',
      border: `1px solid ${msg.type === 'success' ? '#86efac' : '#fca5a5'}`,
      color: msg.type === 'success' ? '#166534' : '#991b1b'
    }}>
      {msg.type === 'success' ? <CheckCircle size={14} /> : <AlertTriangle size={14} />}
      {msg.text}
    </div>
  );

  const FieldLabel = ({ children }: { children: React.ReactNode }) => (
    <div style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
      {children}
    </div>
  );

  const inputCss: React.CSSProperties = {
    fontSize: '12px', padding: '7px 10px', borderRadius: '7px',
    border: '1px solid #e5e7eb', background: '#f9fafb', outline: 'none',
    width: '100%', transition: 'border-color 0.2s'
  };

  const PwInput = ({ field, placeholder }: { field: 'current' | 'new' | 'confirm'; placeholder: string }) => {
    const key = field === 'current' ? 'current_password' : field === 'new' ? 'new_password' : 'confirm_password';
    return (
      <div style={{ position: 'relative' }}>
        <input type={showPw[field] ? 'text' : 'password'} placeholder={placeholder}
          value={pwForm[key]} onChange={(e) => setPwForm({ ...pwForm, [key]: e.target.value })}
          style={{ ...inputCss, paddingRight: '34px' }} />
        <button type="button" onClick={() => setShowPw(p => ({ ...p, [field]: !p[field] }))}
          style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#9ca3af' }}>
          {showPw[field] ? <EyeOff size={13} /> : <Eye size={13} />}
        </button>
      </div>
    );
  };

  return (
    <MainLayout>
      <style>{`
        .settings-nav-item { cursor: pointer; border-radius: 8px; padding: 9px 12px; display: flex; align-items: center; gap: 9px; font-size: 12px; font-weight: 500; color: #6b7280; transition: all 0.15s; margin-bottom: 2px; }
        .settings-nav-item:hover { background: #f3f4f6; color: #111827; }
        .settings-nav-item.active { background: #eff6ff; color: #2563eb; font-weight: 600; }
        .settings-nav-item.active svg { color: #2563eb; }
        .settings-input:focus { border-color: #2563eb !important; background: #fff !important; box-shadow: 0 0 0 3px rgba(37,99,235,0.08); }
        .action-btn { border: none; border-radius: 7px; font-size: 12px; font-weight: 600; padding: 7px 14px; cursor: pointer; display: inline-flex; align-items: center; gap: 5px; transition: all 0.15s; }
        .action-btn:hover { opacity: 0.9; transform: translateY(-1px); }
        .action-btn:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
      `}</style>

      <div style={{ display: 'flex', height: 'calc(100vh - 60px)', background: '#f8fafc', fontSize: '13px' }}>

        {/* Left Sidebar */}
        <div style={{ width: '200px', flexShrink: 0, background: '#fff', borderRight: '1px solid #e5e7eb', padding: '16px 12px', display: 'flex', flexDirection: 'column' }}>

          {/* User Card */}
          <div style={{ background: 'linear-gradient(135deg, #1d4ed8, #4f46e5)', borderRadius: '10px', padding: '12px', marginBottom: '16px', color: 'white' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <div style={{ width: '30px', height: '30px', background: 'rgba(255,255,255,0.2)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '13px' }}>
                {user?.username?.charAt(0).toUpperCase()}
              </div>
              <div>
                <div style={{ fontWeight: '700', fontSize: '12px' }}>{user?.username}</div>
                <div style={{ fontSize: '10px', opacity: 0.75 }}>Trader</div>
              </div>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.12)', borderRadius: '6px', padding: '5px 8px' }}>
              <div style={{ fontSize: '9px', opacity: 0.75, letterSpacing: '0.5px', marginBottom: '1px' }}>DHAN CLIENT ID</div>
              <div style={{ fontWeight: '800', fontSize: '13px', letterSpacing: '0.5px' }}>{user?.dhan_client_id || '—'}</div>
            </div>
          </div>

          {/* Nav */}
          <div style={{ fontSize: '10px', fontWeight: '700', color: '#9ca3af', letterSpacing: '0.8px', marginBottom: '6px', paddingLeft: '4px' }}>SETTINGS</div>
          {navItems.map(({ id, label, icon: Icon }) => (
            <div key={id} className={`settings-nav-item ${activeSection === id ? 'active' : ''}`} onClick={() => setActiveSection(id)}>
              <Icon size={14} />
              {label}
            </div>
          ))}
        </div>

        {/* Main Content */}
        <div style={{ flex: 1, padding: '20px 24px', overflowY: 'auto' }}>

          {/* Section Header */}
          <div style={{ marginBottom: '20px', paddingBottom: '14px', borderBottom: '1px solid #e5e7eb' }}>
            <div style={{ fontWeight: '700', fontSize: '15px', color: '#111827' }}>
              {activeSection === 'tradingHours' && 'Trading Hours'}
              {activeSection === 'exitTimes' && 'Mandatory Exit Times'}
              {activeSection === 'pageVisibility' && 'Page Visibility'}
              {activeSection === 'password' && 'Change Password'}
              {activeSection === 'appearance' && 'Appearance'}
            </div>
            <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
              {activeSection === 'tradingHours' && 'One shared entry window for every feature — NSE, BSE, NY/SX Spread, Manual Spread, Option Selling, Cash Market'}
              {activeSection === 'exitTimes' && 'Force-close open positions past these times, even if SL/Target never hits'}
              {activeSection === 'pageVisibility' && 'Pick which pages show in the left sidebar — hidden pages stay hidden until turned back on here'}
              {activeSection === 'password' && 'Update your account password'}
              {activeSection === 'appearance' && 'Customize your trading interface theme'}
            </div>
          </div>

          {/* ── TRADING HOURS SECTION ── */}
          {activeSection === 'tradingHours' && (
            <div style={{ maxWidth: '420px' }}>
              <MsgBox msg={tradingHoursMsg} />

              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '16px', marginBottom: '12px' }}>
                <FieldLabel>Start Time</FieldLabel>
                <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '8px' }}>
                  No entry is placed by any feature before this time.
                </div>
                <input type="time" className="settings-input"
                  value={tradingHours.startTime}
                  onChange={(e) => setTradingHours(prev => ({ ...prev, startTime: e.target.value }))}
                  style={inputCss} />
              </div>

              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '16px', marginBottom: '12px' }}>
                <FieldLabel>End Time</FieldLabel>
                <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '8px' }}>
                  No entry is placed by any feature after this time. (Separate from the Exit Times below, which force-close positions already open.)
                </div>
                <input type="time" className="settings-input"
                  value={tradingHours.endTime}
                  onChange={(e) => setTradingHours(prev => ({ ...prev, endTime: e.target.value }))}
                  style={inputCss} />
              </div>

              <button className="action-btn" onClick={handleTradingHoursSave} disabled={tradingHoursSaving}
                style={{ background: 'linear-gradient(135deg, #2563eb, #4f46e5)', color: 'white', width: '100%', justifyContent: 'center', padding: '9px' }}>
                <Save size={13} />
                {tradingHoursSaving ? 'Saving...' : 'Save Trading Hours'}
              </button>
            </div>
          )}

          {/* ── EXIT TIMES SECTION ── */}
          {activeSection === 'exitTimes' && (
            <div style={{ maxWidth: '420px' }}>
              <MsgBox msg={exitTimesMsg} />

              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '16px', marginBottom: '12px' }}>
                <FieldLabel>Option Selling + Spread + NSE/BSE (Others)</FieldLabel>
                <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '8px' }}>
                  All open positions in these force-close at this time, every trading day.
                </div>
                <input type="time" className="settings-input"
                  value={exitTimes.spread_others_exit_time}
                  onChange={(e) => setExitTimes(prev => ({ ...prev, spread_others_exit_time: e.target.value }))}
                  style={inputCss} />
              </div>

              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '16px', marginBottom: '12px' }}>
                <FieldLabel>Cash Market</FieldLabel>
                <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '8px' }}>
                  Earlier than the above by design — real intraday equity positions carry the broker's own stricter auto-square-off window.
                </div>
                <input type="time" className="settings-input"
                  value={exitTimes.cash_market_exit_time}
                  onChange={(e) => setExitTimes(prev => ({ ...prev, cash_market_exit_time: e.target.value }))}
                  style={inputCss} />
              </div>

              <button className="action-btn" onClick={handleExitTimesSave} disabled={exitTimesSaving}
                style={{ background: 'linear-gradient(135deg, #2563eb, #4f46e5)', color: 'white', width: '100%', justifyContent: 'center', padding: '9px' }}>
                <Save size={13} />
                {exitTimesSaving ? 'Saving...' : 'Save Exit Times'}
              </button>
            </div>
          )}

          {/* ── PAGE VISIBILITY SECTION ── */}
          {activeSection === 'pageVisibility' && (
            <div style={{ maxWidth: '420px' }}>
              <MsgBox msg={pageVisMsg} />
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '8px' }}>
                {menuItems.map(item => {
                  const forced = ALWAYS_VISIBLE_PATHS.includes(item.path);
                  const visible = forced || !hiddenPaths.has(item.path);
                  return (
                    <div key={item.path} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '8px 8px', borderBottom: '1px solid #f3f4f6',
                    }}>
                      <span style={{ fontSize: '12px', fontWeight: 600, color: visible ? '#111827' : '#9ca3af' }}>
                        {item.label}{forced ? ' (always on)' : ''}
                      </span>
                      <ToggleSwitch
                        checked={visible}
                        disabled={forced || pageVisSavingPath !== null}
                        onChange={() => handlePageVisibilityToggle(item.path, visible)}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── PASSWORD SECTION ── */}
          {activeSection === 'password' && (
            <div style={{ maxWidth: '380px' }}>
              <MsgBox msg={pwMsg} />

              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <FieldLabel>Current Password</FieldLabel>
                  <PwInput field="current" placeholder="Enter your current password" />
                </div>
                <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: '12px' }}>
                  <FieldLabel>New Password</FieldLabel>
                  <PwInput field="new" placeholder="Minimum 6 characters" />
                </div>
                <div>
                  <FieldLabel>Confirm New Password</FieldLabel>
                  <PwInput field="confirm" placeholder="Re-enter new password" />
                </div>
              </div>

              <button className="action-btn" onClick={handleChangePassword} disabled={pwLoading}
                style={{ background: 'linear-gradient(135deg, #2563eb, #4f46e5)', color: 'white', width: '100%', justifyContent: 'center', marginTop: '12px', padding: '9px' }}>
                <Key size={13} />
                {pwLoading ? 'Updating Password...' : 'Update Password'}
              </button>
            </div>
          )}

          {/* ── APPEARANCE SECTION ── */}
          {activeSection === 'appearance' && (
            <div style={{ maxWidth: '480px' }}>
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '16px' }}>
                <ThemeSelector />
              </div>
            </div>
          )}

        </div>
      </div>
    </MainLayout>
  );
};

export default SettingsPage;
